import {
  Callout,
  DetailsList,
  DetailsListLayoutMode,
  FontWeights,
  Link,
  mergeStyleSets,
  SelectionMode,
  Text,
} from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import React from 'react';
import { genkey } from '../../common/constants';
import { TablaGeneralPosiciones, TablaPosiciones } from '../../system/SystemClient';
import ClubImageCircle from '../Common/ClubImageCircle';

const TorneoTabTablaPosiciones = ({
  tablaPosiciones,
  tablaGeneralPosiciones,
  highlightClubId,
}: {
  tablaPosiciones: TablaPosiciones[];
  tablaGeneralPosiciones: TablaGeneralPosiciones[];
  highlightClubId?: string;
}) => {
  const ROW_H = (compact?: boolean) => (compact ? 36 : 42);
  const sameId = (a?: string | number, b?: string | number) =>
    a != null && b != null && String(a) === String(b);

  const isHL = (id?: string | number, hl?: string | number) => sameId(id, hl);

  const getCols = (compact?: boolean, highlightClubId?: string) => {
    const columnsDesktop = [
      {
        key: 'posicion',
        name: '#',
        fieldName: 'posicion',
        minWidth: compact ? 24 : 50,
        maxWidth: compact ? 30 : 60,
        isResizable: true,
        onRender: (item: any) => (
          <span style={{ fontSize: compact ? 13 : 16, lineHeight: `${ROW_H(compact)}px` }}>
            {item.puesto}
          </span>
        ),
      },
      {
        key: 'club',
        name: 'Club',
        fieldName: 'club',
        minWidth: compact ? 180 : 400,
        maxWidth: compact ? 220 : 500,
        isResizable: true,
        onRender: (item: any) => {
          const clubId = item.clubId ?? item.club_id ?? item.idClub; // ← asegura el ID correcto

          return (
            <div style={{ display: 'flex', alignItems: 'center', height: ROW_H(compact), width: '100%' }}>
              <div
                className={isHL(clubId, highlightClubId) ? 'highlight-club highlight-club--mount' : ''}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  height: ROW_H(compact),
                  padding: `0 ${compact ? 0 : 10}px`,
                  borderRadius: 6,
                  boxSizing: 'border-box',
                  minWidth: 0,
                }}
              >
                <div style={{ flex: '0 0 auto' }}>
                  <ClubImageCircle
                    escudo={item.club_escudo}
                    width={compact ? 19 : 30}
                    height={compact ? 19 : 30}
                  />
                </div>
                <span
                  className="team-name"
                  style={{
                    marginLeft: compact ? 6 : 10,
                    fontSize: compact ? 13 : 16,
                    fontWeight: isHL(clubId, highlightClubId) ? 600 : 500,
                    color: '#605e5c',
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  title={item.club}
                >
                  {item.club}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        key: 'PTS',
        name: 'PTS',
        fieldName: 'PTS',
        minWidth: compact ? 36 : 50,
        maxWidth: compact ? 44 : 64,
        isResizable: true,
        onRender: (item: any) => (
          <span style={{ fontSize: compact ? 13 : 16, fontWeight: 700, lineHeight: `${ROW_H(compact)}px` }}>
            {item.puntos}
          </span>
        ),
      },
      {
        key: 'PJ',
        name: 'PJ',
        fieldName: 'PJ',
        minWidth: compact ? 28 : 50,
        maxWidth: compact ? 36 : 64,
        isResizable: true,
        onRender: (item: any) => (
          <span style={{ fontSize: compact ? 13 : 16, lineHeight: `${ROW_H(compact)}px` }}>
            {item.partidosJugados}
          </span>
        ),
      },
      {
        key: 'pg',
        name: 'PG',
        fieldName: 'pg',
        minWidth: compact ? 28 : 50,
        maxWidth: compact ? 36 : 64,
        isResizable: true,
        onRender: (item: any) => (
          <span style={{ fontSize: compact ? 13 : 16, lineHeight: `${ROW_H(compact)}px` }}>
            {item.partidosGanados}
          </span>
        ),
      },
      {
        key: 'PE',
        name: 'PE',
        fieldName: 'PE',
        minWidth: compact ? 28 : 50,
        maxWidth: compact ? 36 : 64,
        isResizable: true,
        onRender: (item: any) => (
          <span style={{ fontSize: compact ? 13 : 16, lineHeight: `${ROW_H(compact)}px` }}>
            {item.partidosEmpatados}
          </span>
        ),
      },
      {
        key: 'PP',
        name: 'PP',
        fieldName: 'PP',
        minWidth: compact ? 28 : 50,
        maxWidth: compact ? 36 : 64,
        isResizable: true,
        onRender: (item: any) => (
          <span style={{ fontSize: compact ? 13 : 16, lineHeight: `${ROW_H(compact)}px` }}>
            {item.partidosPerdidos}
          </span>
        ),
      },
      {
        key: 'GF',
        name: 'GF',
        fieldName: 'GF',
        minWidth: compact ? 28 : 50,
        maxWidth: compact ? 36 : 64,
        isResizable: true,
        onRender: (item: any) => (
          <span style={{ fontSize: compact ? 13 : 16, lineHeight: `${ROW_H(compact)}px` }}>
            {item.golesAFavor}
          </span>
        ),
      },
      {
        key: 'GC',
        name: 'GC',
        fieldName: 'GC',
        minWidth: compact ? 28 : 50,
        maxWidth: compact ? 36 : 64,
        isResizable: true,
        onRender: (item: any) => (
          <span style={{ fontSize: compact ? 13 : 16, lineHeight: `${ROW_H(compact)}px` }}>
            {item.golesEnContra}
          </span>
        ),
      },
      {
        key: 'DF',
        name: 'DF',
        fieldName: 'DF',
        minWidth: compact ? 28 : 50,
        maxWidth: compact ? 36 : 64,
        isResizable: true,
        onRender: (item: any) => (
          <span style={{ fontSize: compact ? 13 : 16, lineHeight: `${ROW_H(compact)}px` }}>
            {item.golesAFavor - item.golesEnContra}
          </span>
        ),
      },
      {
        key: 'PB',
        name: 'PB',
        fieldName: 'PB',
        minWidth: compact ? 28 : 50,
        maxWidth: compact ? 36 : 64,
        isResizable: true,
        onRender: (item: any) => (
          <span style={{ fontSize: compact ? 13 : 16, lineHeight: `${ROW_H(compact)}px` }}>
            {item.puntosBonus}
          </span>
        ),
      },
      {
        key: 'tieneQuitaPuntos',
        name: '',
        fieldName: 'tieneQuitaPuntos',
        minWidth: compact ? 120 : 180,
        maxWidth: compact ? 200 : 260,
        isResizable: true,
        onRender: (item: any) => (
          <span
            className="error-color blinking-icon"
            style={{ fontSize: compact ? 13 : 16, fontWeight: 500, lineHeight: `${ROW_H(compact)}px` }}
          >
            {item.tieneQuitaPuntos ? '(*) Con descuento de puntos' : ''}
          </span>
        ),
      },
    ];

    return columnsDesktop;
  };

  return (
    <>
      {tablaPosiciones.length > 0 ? (
        <>
          {/* =====================================================================
                              VISTA DESKTOP
        =====================================================================*/}
          <div id="viewDesktop" className="ms-hiddenSm">
            <div className="ms-Grid-row ">
              <div className="ms-Grid-col ms-sm12 text-center">
                <CalloutReferenciasTablas />

                <DetailsList
                  className="no-scroll"
                  onShouldVirtualize={() => false}
                  items={tablaPosiciones}
                  columns={getCols(false, highlightClubId)}
                  setKey="set"
                  layoutMode={DetailsListLayoutMode.justified}
                  selectionMode={SelectionMode.none}
                />
              </div>
            </div>
            {tablaGeneralPosiciones
              .filter((item) => item.tipo !== 'F')
              .map((item) => (
                <div className="ms-Grid-row mt-5">
                  <div className="ms-Grid-col ms-sm12 text-center" style={
                                {
                                      padding: 12,
                      background: "gray"
                                }
                              }>
                    <Text variant="mediumPlus" style={{ fontWeight: 500 }}>
                      {item.rotulo}
                    </Text>
                  </div>

                  <div className="ms-Grid-col ms-sm12 text-center">
                    <DetailsList
                      className="no-scroll"
                      onShouldVirtualize={() => false}
                      items={tablaGeneralPosiciones}
                      columns={getCols(false, highlightClubId)}
                      setKey="set"
                      layoutMode={DetailsListLayoutMode.justified}
                      selectionMode={SelectionMode.none}
                    />
                  </div>
                </div>
              ))}
          </div>
          {/* =====================================================================
                              VISTA MOBILE
        =====================================================================*/}

          <div id="viewMobile" className="ms-hiddenMdUp">
            <div className="ms-Grid-row ">
              <div className="ms-Grid-col ms-sm12 text-center">
                <CalloutReferenciasTablas />

                <DetailsList
                  className=""
                  onShouldVirtualize={() => false}
                  items={tablaPosiciones}
                  columns={getCols(true, highlightClubId)}
                  setKey="set"
                  layoutMode={DetailsListLayoutMode.justified}
                  selectionMode={SelectionMode.none}
                />
              </div>
            </div>

            {tablaGeneralPosiciones
              .filter((item) => item.tipo !== 'F')
              .map((item) => (
                <div className="ms-Grid-row mt-5">
                  <div className="ms-Grid-col ms-sm12 text-center">
                    <Text variant="mediumPlus" style={{ fontWeight: 500 }}>
                      {item.rotulo}
                    </Text>
                  </div>

                  <div className="ms-Grid-col ms-sm12 text-center">
                    <DetailsList
                      className="no-scroll"
                      onShouldVirtualize={() => false}
                      items={tablaGeneralPosiciones}
                      columns={getCols(true, highlightClubId)}
                      setKey="set"
                      layoutMode={DetailsListLayoutMode.justified}
                      selectionMode={SelectionMode.none}
                    />
                  </div>
                </div>
              ))}
          </div>
        </>
      ) : (
        <div className="ms-Grid-col ms-sm12 mt-2 text-center">
          <Text variant="medium">Todavía no hay tabla de posiciones para este torneo.</Text>
        </div>
      )}
    </>
  );
};

export const CalloutReferenciasTablas = () => {
  const [isCalloutVisible, setIsCalloutVisible] = React.useState<boolean>(false);
  const toggleIsCalloutVisible = () => setIsCalloutVisible(!isCalloutVisible);
  const buttonId = useId(genkey());
  const labelId = useId(genkey());
  const descriptionId = useId(genkey());

  const styles = mergeStyleSets({
    callout: {
      width: 200,
      maxWidth: '90%',
      padding: '10px 14px',
    },
    title: {
      marginBottom: 12,
      fontWeight: FontWeights.semilight,
    },
    link: {
      display: 'block',
      marginTop: 20,
    },
  });

  return (
    <div className="" style={{ textAlign: 'right' }}>
      <Link id={buttonId} title="Ver referencias" onClick={toggleIsCalloutVisible}>
        Referencias
      </Link>

      {isCalloutVisible && (
        <Callout
          className={styles.callout}
          ariaLabelledBy={labelId}
          ariaDescribedBy={descriptionId}
          role="dialog"
          gapSpace={0}
          target={`#${buttonId}`}
          onDismiss={toggleIsCalloutVisible}
          setInitialFocus
        >
          <Text variant="medium" block className="fw-500" id={labelId}>
            PTS: <span style={{ fontWeight: 400 }}>Puntos totales</span>
          </Text>
          <Text variant="medium" block className="fw-500" id={labelId}>
            PJ: <span style={{ fontWeight: 400 }}>Partidos jugados</span>
          </Text>
          <Text variant="medium" block className="fw-500" id={labelId}>
            PG: <span style={{ fontWeight: 400 }}>Partidos ganados</span>
          </Text>
          <Text variant="medium" block className="fw-500" id={labelId}>
            PE: <span style={{ fontWeight: 400 }}>Partidos empatados</span>
          </Text>
          <Text variant="medium" block className="fw-500" id={labelId}>
            PP: <span style={{ fontWeight: 400 }}>Partidos perdidos</span>
          </Text>
          <Text variant="medium" block className="fw-500" id={labelId}>
            GF: <span style={{ fontWeight: 400 }}>Goles a favor</span>
          </Text>
          <Text variant="medium" block className="fw-500" id={labelId}>
            GC: <span style={{ fontWeight: 400 }}>Goles en contra</span>
          </Text>
          <Text variant="medium" block className="fw-500" id={labelId}>
            DF: <span style={{ fontWeight: 400 }}>Diferencia de goles</span>
          </Text>
          <Text variant="medium" block className="fw-500" id={labelId}>
            PB: <span style={{ fontWeight: 400 }}>Puntos bonus</span>
          </Text>
        </Callout>
      )}
    </div>
  );
};

export default TorneoTabTablaPosiciones;
